import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../components/Seo";
import { apiGet, formatEuro } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { buildBreadcrumbJsonLd, buildProductJsonLd } from "../lib/seo";
import "./Shop.css";

const ProductPage = ({ onAddToCart }) => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const productPath = `/shop/${encodeURIComponent(slug || "")}`;
  const seoTitle = product?.name || "Prodotto shop";
  const seoDescription =
    product?.description || "Scheda prodotto dello shop Panetteria Primavera con disponibilita, prezzo e dettagli.";
  const seoJsonLd = product
    ? [
        buildProductJsonLd(product),
        buildBreadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Shop", path: "/shop" },
          { name: product.name, path: productPath }
        ])
      ]
    : buildBreadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Shop", path: "/shop" }
      ]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    setProduct(null);
    setQty(1);

    const loadProduct = async () => {
      try {
        const data = await apiGet(`/api/products/${slug}`);
        if (mounted) {
          setProduct(data.product || null);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Unable to load product");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      mounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!product) {
      return;
    }

    trackEvent("view_item", {
      currency: "EUR",
      value: (Number(product.price_cents) || 0) / 100,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: (Number(product.price_cents) || 0) / 100,
          quantity: 1
        }
      ]
    });
  }, [product]);

  return (
    <section className="product-page">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={productPath}
        image={product?.image_url}
        type={product ? "product" : "website"}
        jsonLd={seoJsonLd}
      />
      <p>
        <Link className="shop-back-link" to="/shop">
          Torna allo shop
        </Link>
      </p>

      {loading && <p className="shop-state">Caricamento prodotto...</p>}
      {!loading && error && <p className="shop-state shop-error">Errore: {error}</p>}

      {!loading && !error && product && (
        <article className="product-card">
          <div className="product-image-wrap">
            {product.image_url ? (
              <img className="product-image" src={product.image_url} alt={product.name} />
            ) : (
              <div className="product-image product-image-placeholder">No image</div>
            )}
          </div>
          <div className="product-content">
            <h2>{product.name}</h2>
            <p className="product-description">{product.description}</p>
            <div className="product-meta">
              <span className="product-price">{formatEuro(product.price_cents)}</span>
              <span>Peso: {product.weight_grams} g</span>
            </div>
            <div className="product-actions">
              <label htmlFor="product-qty">Quantita</label>
              <input
                id="product-qty"
                className="product-qty-input"
                type="number"
                min="1"
                max="50"
                value={qty}
                onChange={(event) => setQty(Math.max(1, Math.min(50, Number(event.target.value) || 1)))}
              />
              <button className="shop-add-button" onClick={() => onAddToCart?.(product, qty)}>
                Aggiungi al carrello
              </button>
              <Link className="shop-back-link" to="/shop/cart">
                Vai al carrello
              </Link>
            </div>
            <p className="product-note">Pagamento sicuro e spedizione in tutta Italia.</p>
          </div>
        </article>
      )}
    </section>
  );
};

export default ProductPage;
